/**
 * Viking Malt (Finland, with maltings across the Nordics and Baltics) and
 * Gladfield Malt (Dunsandel, New Zealand).
 *
 * Both houses publish colour in EBC, so the °L figure alongside it is the
 * conversion `°L = (EBC + 1.2) / 2.65` rounded to one decimal rather than a
 * second measurement. Viking quotes diastatic power in Windisch-Kolbach and it
 * is converted with `°Lintner = (°WK + 16) / 3.5`; Gladfield quotes figures
 * that are already in the Lintner scale. Neither maltster publishes
 * distilled-water mash pH or titratable buffering, so those fields are `null`
 * and the {@link MaltClass} defaults apply.
 *
 * Viking's own web pages carry prose only: every figure below comes from the
 * per-product **Malt Data Sheet** PDF each page links, and the `url` is the
 * page that linked it. Two of those pages cover a whole family — Caramel and
 * Munich — and their sheets do tabulate each grade separately (a colour band
 * per grade, and for the caramels an extract band per group of grades), so the
 * grades are entered as separate rows. They are entered *without* a link,
 * because a product URL in this catalog belongs to exactly one entry
 * (`product-urls.test.ts`) and one family page cannot honestly stand as the
 * page for nine caramel grades or two Munichs. The one exception is
 * `viking-munich-light`, which already held that link before the rest of the
 * ladder was added.
 *
 * Where Viking quote a specification as `min.` or `max.` rather than a band,
 * that single bound is what is stored — several of the specialities publish
 * colour only as a ceiling — and the row says so and stays `representative`.
 */

import type { Fermentable } from '../../schema/fermentable';

/** Viking Malt and Gladfield malts. @see Fermentable */
export const vikingGladfieldFermentables: readonly Fermentable[] = [
  {
    id: 'gladfield-ale',
    name: 'Ale Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 5,
    colorLovibond: 2.6,
    colorEbc: 5.7,
    diastaticPowerLintner: 47,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Gladfield's everyday ale base, kilned a shade past their pilsner into light biscuit. Enough enzyme to carry a grist alone and the malt most New Zealand ales are built on.",
    tags: ['base', 'ale', 'new-zealand', 'biscuit'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-aurora',
    name: 'Aurora Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 5,
    colorLovibond: 17.8,
    colorEbc: 46,
    diastaticPowerLintner: 47,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A deeply kilned sub-base at 46 EBC that still converts itself, so it can be the whole grist of an amber or a dark lager rather than a seasoning. Rich and bready without the caramel of a crystal.',
    tags: ['kilned', 'amber', 'new-zealand', 'malty'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-big-o-oats',
    name: 'Big O Malted Oats',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 58,
    moisturePercent: 5,
    colorLovibond: 1.7,
    colorEbc: 3.3,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted oats rather than flaked, bringing their own enzymes along with the silky body oats are used for. Extract is low by nature at 55-61 %, and Gladfield publish no diastatic figure, so that one is class-typical.',
    tags: ['oat', 'adjunct', 'new-zealand', 'body', 'hazy'],
    provenance: 'representative',
  },
  {
    id: 'gladfield-biscuit',
    name: 'Biscuit Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 25.0,
    colorEbc: 65,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Toasted to fresh bread crust and digestive biscuit without any roast bitterness. Five to fifteen percent gives an ale a baked-malt top note that a crystal cannot.',
    tags: ['biscuit', 'toasted', 'new-zealand', 'bready'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-black-forest-rye',
    name: 'Black Forest Rye Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 71,
    moisturePercent: 4.5,
    colorLovibond: 33.7,
    colorEbc: 88,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rye kilned dark into pumpernickel and dark bread, and the malt to reach for when a rye beer wants depth rather than color. Ten percent is the ceiling and it does not need all of it.',
    tags: ['rye', 'kilned', 'new-zealand', 'dark', 'spicy'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-brown',
    name: 'Brown Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 64.6,
    colorEbc: 170,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Paler than most brown malts at 170 EBC, sitting between biscuit and chocolate: dry, nutty and toasted rather than roasted. Traditional in a porter at 5-15 %.',
    tags: ['brown', 'toasted', 'new-zealand', 'nutty', 'porter'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-chocolate-rye',
    name: 'Chocolate Rye Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 74,
    moisturePercent: 3,
    colorLovibond: 358.9,
    colorEbc: 950,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Rye roasted to chocolate depth, which keeps the grain's pepper under the cocoa. Five percent is the ceiling. Gladfield's own table gives a typical color below the band it quotes, so the figure here is the band's midpoint.",
    tags: ['roasted', 'rye', 'new-zealand', 'chocolate', 'spicy'],
    provenance: 'representative',
  },
  {
    id: 'gladfield-crystal-rye',
    name: 'Crystal Rye Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 26.9,
    colorEbc: 70,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rye crystallized rather than barley: bread crust and a sweet spice no barley crystal reaches, at a color pale enough to use freely in a rye ale.',
    tags: ['crystal', 'rye', 'new-zealand', 'spicy', 'bread-crust'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-crystal-wheat',
    name: 'Crystal Wheat Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 10,
    colorLovibond: 19.7,
    colorEbc: 51,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat stewed into a crystal, which gives the caramel sweetness of one and the foam of the other. Gladfield publish no extract for it, so that figure is class-typical for a crystal of this depth.',
    tags: ['crystal', 'wheat', 'new-zealand', 'body', 'head-retention'],
    provenance: 'representative',
  },
  {
    id: 'gladfield-dark-chocolate',
    name: 'Dark Chocolate Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 71,
    moisturePercent: 4.5,
    colorLovibond: 491.0,
    colorEbc: 1300,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Roasted on past cocoa into coffee and a dry, sharp finish, and dark enough that a percent or two sets a beer's color on its own.",
    tags: ['roasted', 'chocolate', 'new-zealand', 'dark', 'coffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-dark-crystal',
    name: 'Dark Crystal Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 6,
    colorLovibond: 75.9,
    colorEbc: 200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest crystal in the range, running to raisin and burnt sugar rather than toffee. It deepens a bock or a dark ale at 5-10 % and dominates well before its stated ceiling.',
    tags: ['crystal', 'caramel', 'new-zealand', 'raisin', 'dark'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-eclipse-wheat',
    name: 'Eclipse Wheat Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 74,
    moisturePercent: 2.5,
    colorLovibond: 528.8,
    colorEbc: 1400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat roasted to black, and husk-free, so it colors as hard as a black malt while arguing far less. One to ten percent, and the low end goes a long way.',
    tags: ['roasted', 'wheat', 'new-zealand', 'black', 'smooth'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-german-pilsner',
    name: 'German Pilsner Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 5,
    colorLovibond: 2.0,
    colorEbc: 4,
    diastaticPowerLintner: 73,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Continental-style pilsner malt grown and malted in Canterbury, at 240 WK the most enzymatic of the base range. Clean and pale enough to leave the color entirely to what follows.',
    tags: ['base', 'pilsner', 'new-zealand', 'lager'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-golden-oat',
    name: 'Gladfield Golden Oat',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 9.5,
    colorEbc: 24,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Oats taken to a golden 24 EBC, which puts a light nutty sweetness alongside the silky body oats are bought for. Up to 20 %, and it does more for mouthfeel than any figure suggests.',
    tags: ['oat', 'new-zealand', 'golden', 'body', 'smooth'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-lager-light',
    name: 'Lager Light Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 5,
    colorLovibond: 1.5,
    colorEbc: 2.7,
    diastaticPowerLintner: 73,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest malt Gladfield make at 2.7 EBC, for lagers that have to look almost colorless. Same enzyme package as their pilsner malt with even less to say for itself, which is the point.',
    tags: ['base', 'lager', 'new-zealand', 'pale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-light-chocolate',
    name: 'Light Chocolate Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 71,
    moisturePercent: 4.5,
    colorLovibond: 358.9,
    colorEbc: 950,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The paler of the two chocolate malts, giving cocoa and light coffee without the ash that comes further up the scale. Two to six percent in a porter or a stout.',
    tags: ['roasted', 'chocolate', 'new-zealand', 'cocoa'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-maize',
    name: 'Maize Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 67.5,
    moisturePercent: 4,
    colorLovibond: 1.4,
    colorEbc: 2.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted maize rather than flaked or grits, which is unusual — most maize reaches a brewhouse unmalted. Light and clean, and Gladfield allow it to the whole grist. Extract is published as a wide 60-75 % band and the figure here is its middle.',
    tags: ['maize', 'adjunct', 'new-zealand', 'light'],
    provenance: 'representative',
  },
  {
    id: 'gladfield-manuka-smoked',
    name: 'Manuka Smoked Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 5,
    colorLovibond: 1.9,
    colorEbc: 3.9,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Gladfield smoke their distilling malt over West Coast manuka, and it is the only manuka-smoked malt anyone makes. The smoke is floral and sweet where beech is savory and peat is medicinal, and it sits over a pale, fully enzymatic base — so it can be the whole grist of a rauchbier or a percent or two of seasoning, which is the range Gladfield themselves quote.',
    tags: ['smoked', 'manuka', 'new-zealand', 'base', 'rauchbier', 'floral'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-medium-crystal',
    name: 'Medium Crystal Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 6,
    colorLovibond: 40.1,
    colorEbc: 105,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle of three crystal grades, and the one most amber and red ales want: toffee and caramel with enough color to matter. Up to a quarter of the grist, though 5-15 % is where it usually sits.',
    tags: ['crystal', 'caramel', 'new-zealand', 'toffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-munich',
    name: 'Munich Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 5,
    colorLovibond: 5.9,
    colorEbc: 14.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Munich at 14.5 EBC with 160 WK behind it, so it converts itself comfortably and can be the whole grist of a dunkel or a maerzen. Bread crust and malt depth without caramel sweetness.',
    tags: ['munich', 'kilned', 'new-zealand', 'malty'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-redback',
    name: 'RedBack Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 6.5,
    colorLovibond: 25.0,
    colorEbc: 65,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Gladfield's red malt, built to put a genuine copper-red into a beer at a modest 15 % rather than by piling on dark crystal. Caramel-sweet with a clean finish.",
    tags: ['crystal', 'red', 'new-zealand', 'caramel'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-redback-wheat',
    name: 'Redback Wheat Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6.5,
    colorLovibond: 25.0,
    colorEbc: 65,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The wheat version of RedBack, for red beers that also want the head a wheat crystal builds. Only its typical color and moisture are published, so extract here is class-typical.',
    tags: ['crystal', 'wheat', 'red', 'new-zealand'],
    provenance: 'representative',
  },
  {
    id: 'gladfield-roasted-wheat',
    name: 'Roasted Wheat Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 71,
    moisturePercent: 4.5,
    colorLovibond: 208.0,
    colorEbc: 550,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat roasted to a middle depth, husk-free and so smoother than a barley malt of the same color. It dries a dunkles weissbier or a porter without adding harshness.',
    tags: ['roasted', 'wheat', 'new-zealand', 'dry', 'smooth'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-rye',
    name: 'Rye Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 6,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'New Zealand malted rye at 81 % extract, bringing pepper and a slick mouthfeel. Gladfield suggest starting at 10 % and going to 50; it is husk-free, so the lauter needs planning. No diastatic figure is published, so that one is class-typical.',
    tags: ['rye', 'new-zealand', 'spicy', 'base'],
    provenance: 'representative',
  },
  {
    id: 'gladfield-supernova',
    name: 'Supernova Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71,
    moisturePercent: 6,
    colorLovibond: 43.5,
    colorEbc: 114,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A crystal taken to an unusually intense caramel for its color, which is why Gladfield allow it to 25 % where a malt this dark would normally stop at 10. Rich rather than merely sweet.',
    tags: ['crystal', 'caramel', 'new-zealand', 'intense'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-toffee',
    name: 'Toffee Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 7.5,
    colorLovibond: 5.0,
    colorEbc: 12,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest crystal Gladfield make at 12 EBC, adding toffee sweetness and body with almost no color. The one to use when a pale beer needs filling out and darkening is not an option.',
    tags: ['crystal', 'caramel', 'new-zealand', 'toffee', 'pale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-wheat',
    name: 'Wheat Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 6,
    colorLovibond: 2.0,
    colorEbc: 4.1,
    diastaticPowerLintner: 73,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale wheat malt at 81 % extract and 240 WK — enzymatic enough to carry itself and most of a weissbier. Husk-free, so add rice hulls above about half the bill.',
    tags: ['wheat', 'base', 'new-zealand', 'head-retention'],
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-pilsner',
    name: 'Pilsner Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 73,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Nordic two-row pilsner malt, kilned pale and cleanly modified. Slow-grown northern barley gives it a soft, faintly sweet grain character that suits pale lagers and leaves ample enzyme for adjuncts.',
    tags: ['base', 'lager', 'pilsner', 'nordic', 'finnish'],
    url: 'https://vikingmalt.com/malts/pilsner-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned a step warmer than the pilsner for a fuller, lightly biscuity base without meaningful color. A sensible whole-grist malt for pale and hoppy ales where the malt should support rather than lead.',
    tags: ['base', 'ale', 'nordic', 'finnish'],
    url: 'https://vikingmalt.com/malts/viking-pale-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-vienna',
    name: 'Vienna Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 3.5,
    colorEbc: 8,
    diastaticPowerLintner: 67,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Amber-kilned malt that keeps enough diastatic power to convert an entire mash on its own. Adds golden color and a toasted-bread sweetness to Vienna lager, Märzen and amber ales.',
    tags: ['base', 'kilned', 'vienna', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-vienna-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-munich-light',
    name: 'Munich Malt Light',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.5,
    moisturePercent: 4.5,
    colorLovibond: 6.1,
    colorEbc: 15,
    diastaticPowerLintner: 56,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The paler of the two Viking Munich grades, still comfortably self-converting. Use it as the whole grist for a dunkel or bock, or at 10–30 % to give any beer a deeper bready malt backbone.',
    tags: ['base', 'kilned', 'munich', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-munich-malts/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-wheat',
    name: 'Wheat Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 5.5,
    colorLovibond: 2.2,
    colorEbc: 4.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Husk-free malted wheat with high extract and high protein. Carries the soft body and dense head of a weissbier at 50–70 % of the grist, and improves foam anywhere it turns up at 5–15 %.',
    tags: ['base', 'wheat', 'nordic', 'head-retention'],
    url: 'https://vikingmalt.com/malts/viking-wheat-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-caramel-100',
    name: 'Caramel Malt 100',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 38.3,
    colorEbc: 100,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Mid-range caramel malt, stewed to crystallize its sugars before kilning. Contributes toffee sweetness, unfermentable body and an amber cast; 3–10 % covers most amber and dark lagers.',
    tags: ['crystal', 'caramel', 'nordic', 'amber'],
    provenance: 'representative',
  },
  {
    id: 'viking-chocolate',
    name: 'Chocolate Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Barley malt drum-roasted to a deep brown, giving cocoa and light coffee notes with a firm roast edge. Two to five per cent is usually enough to darken and season a porter or stout.',
    tags: ['roasted', 'chocolate', 'nordic', 'stout'],
    url: 'https://vikingmalt.com/malts/viking-chocolate-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-pilsner-zero',
    name: 'Pilsner Zero Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 73,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pilsner malt from null-LOX barley: the lipoxygenase that drives staling is simply absent, which measurably lengthens flavor stability. Kilned to 80–85 °C and in every other respect a straight pilsner malt. Viking publish no diastatic power for it, so the figure here is the one their ordinary pilsner malt carries.',
    tags: ['base', 'pilsner', 'lager', 'null-lox', 'flavour-stability', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-pilsner-zero-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-golden-ale',
    name: 'Golden Ale Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 4.8,
    colorEbc: 11.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Highly modified ale malt kilned to a golden 9–14 EBC, tasting of cookie, nut and malt sweetness. Half a grist of it, or a few percent to pull a regular lager towards gold. Viking describe its enzymatic potential as reduced without publishing a figure, so it is treated as spent here and the grist should convert it.',
    tags: ['kilned', 'ale', 'golden', 'nutty', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-golden-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-munich-dark',
    name: 'Munich Malt Dark',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 8.8,
    colorEbc: 22.1,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darker Viking Munich grade, cured at 110–120 °C for the nutty, bready depth a dunkel or a bock is built on. Viking warn that the enzyme activity is clearly reduced but publish no figure for either grade, so the diastatic power here is class-typical for a dark Munich — leave some conversion capacity elsewhere in the grist.',
    tags: ['kilned', 'munich', 'dark', 'malty', 'nordic'],
    provenance: 'representative',
  },
  {
    id: 'viking-red-active',
    name: 'Red Active Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 13.7,
    colorEbc: 35.1,
    diastaticPowerLintner: 62,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A single-variety malt cured above 100 °C that still holds 200 WK, which is remarkable at 30–40 EBC: it can be the whole grist and convert adjuncts besides. Clean, malty and nutty, and it reddens a beer without any dark crystal being dragged in to do it.',
    tags: ['kilned', 'red', 'malty', 'high-enzyme', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-red-active-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-red-ale',
    name: 'Red Ale Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 26.9,
    colorEbc: 70.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Melanoidin-rich aroma malt made on a controlled Maillard program, for reddish color and a long malty finish with roast and nut behind it. Slightly acidic, and Viking cap it at a fifth of the grist. They call its amylolytic activity low without quantifying it, so it is treated as spent here.',
    tags: ['kilned', 'melanoidin', 'red', 'aroma', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-red-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-cookie',
    name: 'Cookie Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 19.3,
    colorEbc: 49.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pilsner malt roasted rather than stewed, landing at 40–60 EBC with popcorn and biscuit where a caramel malt of the same color would give toffee. Viking put its amylolytic activity at negligible and keep the dose under 20 %; a couple of percent already tells.',
    tags: ['kilned', 'biscuit', 'roasted-cereal', 'golden', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-cookie-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-dextrin',
    name: 'Dextrin Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 6.5,
    colorLovibond: 4.6,
    colorEbc: 11,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned hard and then dried long and slow rather than stewed, which is how it reaches toffee, caramel and cookie while staying at 8–14 EBC. Bought for body and a rounder finish at up to 30 % of the grist. Viking publish only a 200 WK ceiling for its diastatic power and call the activity modest, so it is treated as spent here.',
    tags: ['kilned', 'dextrin', 'body', 'toffee', 'head-retention', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-dextrin-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-caramel-pale',
    name: 'Caramel Pale',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 7.5,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A genuine caramel malt, saccharified inside the kernel like the rest of the family, but kilned only as far as 6–10 EBC. Sweetness, body and foam for a beer that has to stay straw — the grade to use where even Caramel 30 would show.',
    tags: ['crystal', 'caramel', 'pale', 'body', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-caramel-pale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-cara-body',
    name: 'Cara Body Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 7,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A chit-type malt whose starch is modified for viscosity rather than color, so the beer finishes a point or two higher with a fuller mouthfeel and a firmer head. Clean, with a distinct cereal note; up to 10 % of any grist. Viking publish color only as a ceiling of 8 EBC, so the figure here is that ceiling and not a measurement.',
    tags: ['crystal', 'cara', 'body', 'head-retention', 'chit', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-cara-body-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-caramel-30',
    name: 'Caramel Malt 30',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 7,
    colorLovibond: 11.8,
    colorEbc: 30.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest numbered grade, stewed from green malt taken off germination before kilning. Light toffee, a rounder body and better foam at 25–35 EBC, which a pale ale or a helles can carry without turning amber.',
    tags: ['crystal', 'caramel', 'light', 'toffee', 'nordic'],
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-caramel-50',
    name: 'Caramel Malt 50',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 19.3,
    colorEbc: 49.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Caramel sweetness with the color to match at 40–60 EBC, and the grade most amber ales and bitters reach for first. Five to ten percent gives the toffee note without the raisin edge the darker grades bring.',
    tags: ['crystal', 'caramel', 'amber', 'toffee', 'nordic'],
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-caramel-150',
    name: 'Caramel Malt 150',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 57.1,
    colorEbc: 150.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Copper-red and firmly caramel, past toffee and into dried fruit. Three to eight percent sets the color of a red ale or a dark lager and carries most of its sweetness, so it is easy to overshoot.',
    tags: ['crystal', 'caramel', 'red', 'dried-fruit', 'nordic'],
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-caramel-200',
    name: 'Caramel Malt 200',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 75.9,
    colorEbc: 199.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Raisin, plum and burnt sugar rather than caramel, at the depth where a crystal starts to read as roast. A couple of percent rounds out a bock, a dubbel or a winter ale.',
    tags: ['crystal', 'caramel', 'dark', 'raisin', 'nordic'],
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-caramel-300',
    name: 'Caramel Malt 300',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 6,
    colorLovibond: 113.7,
    colorEbc: 300.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dark caramel made in Poland only, dry and slightly bitter under the dried-fruit sweetness. Used a percent or two at a time for color and depth in stouts and strong dark ales.',
    tags: ['crystal', 'caramel', 'dark', 'bitter-sweet', 'nordic'],
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-caramel-400',
    name: 'Caramel Malt 400',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 68,
    moisturePercent: 6,
    colorLovibond: 151.4,
    colorEbc: 400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Stewed as far as a caramel malt usefully goes, with the burnt-sugar bitterness of a light roast and none of its ash. One to three percent for color and a dry, dark sweetness.',
    tags: ['crystal', 'caramel', 'dark', 'burnt-sugar', 'nordic'],
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-caramel-700',
    name: 'Caramel Malt 700',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 68,
    moisturePercent: 6,
    colorLovibond: 264.6,
    colorEbc: 700,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest grade on the caramel ladder, made only in Poland, and closer in use to a chocolate malt than to a crystal: color first, with a dry burnt sweetness behind it. Under two percent is usually enough.',
    tags: ['crystal', 'caramel', 'very-dark', 'colour-malt', 'nordic'],
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-coffee',
    name: 'Coffee Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 5,
    colorLovibond: 151.4,
    colorEbc: 400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pilsner malt roasted dry to 350–450 EBC, and Viking are careful to say the name is about the color rather than the flavor — though coffee and dark chocolate do arrive as the dose climbs. Deep red-brown at a couple of percent; under 5–10 % in porters and stouts.',
    tags: ['roasted', 'coffee', 'red-brown', 'porter', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-coffee-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 63,
    moisturePercent: 5,
    colorLovibond: 396.7,
    colorEbc: 1050.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley roasted past 200 °C with rather more care than the black malt gets, and harsher and more bitter than it for all that. The dry espresso finish of an Irish stout; 3–8 % is the working band.',
    tags: ['roasted', 'unmalted', 'stout', 'coffee', 'dry', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-roasted-barley/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-black',
    name: 'Black Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 5,
    colorLovibond: 509.9,
    colorEbc: 1350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest malt Viking make, roasted from pilsner malt past 200 °C, where the color arrives in the last few minutes of the drum. Burnt and smoky but less acrid than their roasted barley: 1–2 % moves the color with little change in flavor, and beyond 10 % is rare.',
    tags: ['roasted', 'black', 'colour-malt', 'stout', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-black-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'viking-smoked',
    name: 'Smoked Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 9,
    colorLovibond: 4.2,
    colorEbc: 9.9,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 90,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt smoked over apple, cherry, pear or beech wood, so the smoke reads fruity and mild rather than the savory beechwood of a Franconian rauchmalz. Total phenols run 4–10 ppm and the low process temperature leaves the enzymes intact, which is why Viking allow it to 90 % of the grist. No diastatic figure is published, so that one is class-typical.',
    tags: ['smoked', 'fruitwood', 'rauchbier', 'kilned', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-smoked-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-smoked-wheat',
    name: 'Smoked Wheat Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 9,
    colorLovibond: 4.2,
    colorEbc: 9.9,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat malt smoked over Polish oak, which is the grist grodziskie is made of — a pale, tart, smoky wheat beer. The oak smoke is delicate at 4–10 ppm phenols, and the malt is husk-free, so plan the lauter above about half the bill. No diastatic figure is published, so that one is class-typical for wheat malt.',
    tags: ['smoked', 'wheat', 'oak', 'grodziskie', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-smoked-wheat-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-lightly-peated',
    name: 'Lightly Peated Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 9,
    colorLovibond: 4.2,
    colorEbc: 9.9,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt peat-cured to a restrained 3–10 ppm of phenol, made for whisky but brewable, and otherwise a pilsner malt with a medicinal edge. Peat asserts itself in beer far sooner than fruitwood smoke does, so a fifth of the grist is already plenty. Viking publish no diastatic power, so that figure is class-typical.',
    tags: ['smoked', 'peated', 'whisky', 'kilned', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-lightly-peated-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-rye',
    name: 'Rye Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 6,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 7,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted rye kilned much as pilsner malt is, at 72–80 °C, giving bread and honey with the usual rye pepper behind it. Viking recommend staying under 7 % of the grist: rye is husk-free and slick, and past that the lauter stops running. No diastatic power is published, so that figure is class-typical for malted rye.',
    tags: ['rye', 'base', 'spicy', 'honey', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-rye-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-oat',
    name: 'Oat Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 58,
    moisturePercent: 7,
    colorLovibond: 2.2,
    colorEbc: 4.6,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Traditionally malted oats with the large husk left on, which is why the published extract stops at 58 % — the hull yields nothing, though it does keep the bed loose. Soft, creamy and balancing at up to 20 %, occasionally 30. Viking publish no diastatic power, so that figure is class-typical for oat malt.',
    tags: ['oat', 'adjunct', 'body', 'creamy', 'hull-on', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-oat-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-pot-still-rye',
    name: 'Pot Still Rye Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83.5,
    moisturePercent: 7,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "A distiller's rye malt, steeped and kilned to hold 280 WK — enough enzyme to carry a rye pot-still mash on its own — and at 83.5 % the highest extract in the Viking range. Brewable, with the husk-free lauter problem every rye has. Viking publish no color for it, so the figure here is the one their brewing rye malt carries.",
    tags: ['rye', 'distilling', 'high-enzyme', 'high-extract', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-pot-still-rye-malt/',
    provenance: 'representative',
  },
  {
    id: 'viking-enzyme',
    name: 'Enzyme Malt',
    maker: 'Viking Malt',
    origin: 'Finland',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 76,
    moisturePercent: 7,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 182,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "A high-enzyme malt from two- or six-row barley, germinated and kilned to preserve amylase rather than to build flavor: 620 WK against a pilsner malt's 240. Its job is to convert a grist heavy in unmalted adjuncts, or to mash a grain whisky. Viking publish no color, so that figure is class-typical for a pale distilling malt.",
    tags: ['base', 'high-enzyme', 'distilling', 'adjunct-conversion', 'nordic'],
    url: 'https://vikingmalt.com/malts/viking-enzyme-malt/',
    provenance: 'representative',
  },
  {
    id: 'gladfield-pilsner',
    name: 'Pilsner Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale, lightly kilned base malt from South Island barley, malted on a long germination to keep the flavor delicate. Clean and low in color, with plenty of enzyme in reserve for a lager grist.',
    tags: ['base', 'lager', 'pilsner', 'new-zealand'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-american-ale',
    name: 'American Ale Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The house answer to a US two-row: fully modified, moderately kilned and deliberately neutral. Built as a single-malt base for hop-forward pale ales and IPAs where the malt should stay out of the way.',
    tags: ['base', 'ale', 'ipa', 'new-zealand'],
    provenance: 'spec-sheet',
  },
  {
    id: 'gladfield-gladiator-ale',
    name: 'Gladiator Ale Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 2.5,
    colorEbc: 5.4,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A low-protein, high-extract ale malt cut from the plumpest barley grades, which is why the yield sits above the house pilsner. Gives a rounder malt sweetness than the American Ale malt while staying pale.',
    tags: ['base', 'ale', 'high-extract', 'new-zealand'],
    provenance: 'representative',
  },
  {
    id: 'gladfield-vienna',
    name: 'Vienna Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.5,
    moisturePercent: 4.5,
    colorLovibond: 3.9,
    colorEbc: 9.1,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned warm enough to build light Maillard character but not so far that it loses the ability to convert itself. Adds a golden hue and gentle toast; happy as a full grist or as 10–40 % of an amber beer.',
    tags: ['base', 'kilned', 'vienna', 'new-zealand'],
    provenance: 'representative',
  },
  {
    id: 'gladfield-light-crystal',
    name: 'Light Crystal Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 26.9,
    colorEbc: 70,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest of the Gladfield crystal grades, stewed in the drum to leave a clean honey-and-toffee sweetness. Adds body and a golden-amber lift to pale ales without dragging the color towards copper.',
    tags: ['crystal', 'caramel', 'light', 'new-zealand'],
    provenance: 'representative',
  },
  {
    id: 'gladfield-shepherds-delight',
    name: 'Shepherds Delight Malt',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 226.9,
    colorEbc: 600,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A roasted malt tuned for color rather than bitterness, throwing a deep red-copper hue and a mild toffee-roast note. The usual tool for reddening an amber ale or ESB at 2–5 % without the coffee bite of chocolate malt.',
    tags: ['roasted', 'red', 'colour', 'new-zealand'],
    provenance: 'representative',
  },
  {
    id: 'gladfield-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Gladfield',
    origin: 'New Zealand',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 472.2,
    colorEbc: 1250,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley roasted hard, delivering the dry espresso bitterness that defines an Irish stout. Sharper and more astringent than chocolate malt, so 3–8 % is the working band.',
    tags: ['roasted', 'unmalted', 'stout', 'coffee', 'new-zealand'],
    provenance: 'representative',
  },
];
